import React, { createContext, useContext, useState, useEffect } from 'react';
const safeGet = (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } };
const http = async (method, url, { body, headers, params } = {}) => {
  const qs = params ? `?${new URLSearchParams(params)}` : '';
  const res = await fetch(url + qs, { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(headers || {}) }, body: body ? JSON.stringify(body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const err = new Error(`HTTP ${res.status}`); err.response = { status: res.status, data }; throw err; }
  return { data };
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeGet('token'));
  const [activeDiscounts, setActiveDiscounts] = useState({ global_discount: 0, category_discounts: [] });

  // Charger les remises actives
  useEffect(() => {
    const loadActiveDiscounts = async () => {
      try {
        const response = await http('GET', `${API}/discounts/active`);
        setActiveDiscounts(response.data);
      } catch (error) {
        console.error('Erreur chargement remises:', error);
      }
    };
    loadActiveDiscounts();
  }, []);

  // Vérifier le token au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = safeGet('token');
      if (savedToken) {
        try {
          const response = await http('GET', `${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          console.error('Token invalide:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const register = async (userData) => {
    try {
      const response = await http('POST', `${API}/auth/register`, { body: userData });
      const data = response.data;
      
      // Si le compte est en attente de validation
      if (data.status === 'pending') {
        return { 
          success: true, 
          pending: true,
          message: data.message 
        };
      }
      
      // Si le compte est approuvé immédiatement (admin)
      if (data.status === 'approved' && data.access_token) {
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        return { success: true, user: data.user };
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'inscription' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await http('POST', `${API}/auth/login`, { body: { email, password } });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de la connexion' 
      };
    }
  };

  const logout = () => {
    try { localStorage.removeItem('token'); localStorage.removeItem('ledzone_cart'); } catch (e) { /* stockage bloqué */ }
    setToken(null);
    setUser(null);
  };

  // Fonction pour tracker les activités utilisateur
  const trackActivity = async (activityType, details = {}) => {
    if (!token) return;
    
    try {
      await http('POST', `${API}/track/activity`, {
        params: { activity_type: activityType },
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Erreur tracking activité:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    hasPriceAccess: user?.has_price_access || false,
    userDiscount: user?.discount_percent || 0, // Rabais personnalisé utilisateur
    userCategoryDiscounts: user?.category_discounts || [], // Rabais par catégorie de l'utilisateur
    globalDiscount: activeDiscounts.global_discount || 0, // Rabais global
    categoryDiscounts: activeDiscounts.category_discounts || [], // Rabais par catégorie (global)
    userRole: user?.role || 'client', // Rôle: super_admin, admin, client
    isSuperAdmin: user?.role === 'super_admin' || user?.email === 'info@ledzone.ch',
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin' || user?.is_admin,
    register,
    login,
    logout,
    trackActivity,
    // Helper pour calculer le prix avec tous les rabais applicables
    getDiscountedPrice: (price, category = null) => {
      if (!price || price <= 0) return price;
      
      let totalDiscount = 0;
      
      // 1. Remise utilisateur globale (prioritaire)
      const userDiscountValue = user?.discount_percent || 0;
      if (userDiscountValue > 0) {
        totalDiscount = userDiscountValue;
      } else {
        // 2. Remise utilisateur par catégorie
        if (category && user?.category_discounts?.length > 0) {
          const userCatDiscount = user.category_discounts.find(
            d => d.category === category && d.discount_percent > 0
          );
          if (userCatDiscount) {
            totalDiscount = userCatDiscount.discount_percent;
          }
        }
        
        // 3. Remise globale par catégorie (paramètres boutique)
        if (totalDiscount === 0 && category) {
          const catDiscount = activeDiscounts.category_discounts?.find(
            d => d.category === category && d.is_active
          );
          if (catDiscount) {
            totalDiscount = catDiscount.discount_percent;
          }
        }
        
        // 4. Remise globale (si rien d'autre)
        if (totalDiscount === 0 && activeDiscounts.global_discount > 0) {
          totalDiscount = activeDiscounts.global_discount;
        }
      }
      
      if (totalDiscount > 0) {
        return price * (1 - totalDiscount / 100);
      }
      return price;
    },
    // Helper pour obtenir la remise applicable à un produit
    getApplicableDiscount: (category = null) => {
      // 1. Remise utilisateur globale (prioritaire)
      const userDiscountValue = user?.discount_percent || 0;
      if (userDiscountValue > 0) {
        return { percent: userDiscountValue, type: 'user', label: 'Remise personnelle' };
      }
      
      // 2. Remise utilisateur par catégorie
      if (category && user?.category_discounts?.length > 0) {
        const userCatDiscount = user.category_discounts.find(
          d => d.category === category && d.discount_percent > 0
        );
        if (userCatDiscount) {
          return { percent: userCatDiscount.discount_percent, type: 'user_category', label: `Remise ${category}` };
        }
      }
      
      // 3. Remise globale par catégorie (boutique)
      if (category) {
        const catDiscount = activeDiscounts.category_discounts?.find(
          d => d.category === category && d.is_active
        );
        if (catDiscount) {
          return { percent: catDiscount.discount_percent, type: 'category', label: `Remise ${category}` };
        }
      }
      
      // 4. Remise globale
      if (activeDiscounts.global_discount > 0) {
        return { percent: activeDiscounts.global_discount, type: 'global', label: 'Remise boutique' };
      }
      
      return { percent: 0, type: null, label: null };
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
